import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { Icon } from '@/common/components/Icons/Icon';
import { PageHeader } from '@/common/components/PageHeader/PageHeader';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { ROUTES } from '@/common/constants/routes';
import { useAuth } from '@/common/contexts/authentication/AuthProvider';
import { useAuthenticationContext } from '@/common/contexts/authentication/AuthenticationContext';
import { BiometricsType, useBiometrics } from '@/common/hooks/useBiometrics';
import { AuthFormLayout } from '@/common/layouts/AuthLayout/AuthLayout';
import { notify } from '@/common/utils/toast/toast';
import { usePageViewTracking } from '@/web/hooks/usePageViewTracking';
import * as Device from 'expo-device';
import { useRouter } from 'expo-router';

export function BiometricsPage() {
  usePageViewTracking('biometrics_page');
  const router = useRouter();
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const { biometricsType, biometricsAvailable, enableBiometrics } =
    useBiometrics();
  const authenticationContext = useAuthenticationContext();

  const onPressDismiss = async () => {
    try {
      const { authState } = authenticationContext;

      if (authState && 'user' in authState) {
        if (Device.isDevice) return router.navigate(ROUTES.NOTIFICATIONS);
        await signIn(authState);
      } else {
        router.navigate(ROUTES.SIGNIN);
      }
    } catch (parseError) {
      throw new Error('Invalid user data format');
    }
  };

  const onActivateBiometrics = async () => {
    try {
      const result = await enableBiometrics();
      if (result) {
        const { authState } = authenticationContext;
        if (!authState || !('user' in authState)) {
          throw new Error('User data is missing');
        }

        try {
          if (Device.isDevice) return router.navigate(ROUTES.NOTIFICATIONS);

          await signIn(authState);
        } catch (parseError) {
          throw new Error('Invalid user data format');
        }
      }
    } catch (err: any) {
      notify({
        type: 'error',
        message: (
          <Translate
            i18nKey={
              'authentication.biometrics.errors.biometrics_activation_failed'
            }
          />
        ),
      });
    }
  };

  return (
    <AuthFormLayout
      footer={
        <Stack gap="md">
          <Button
            type="submit"
            onPress={onActivateBiometrics}
            icon={
              <Icon
                icon={
                  biometricsType === BiometricsType.FACE_ID
                    ? 'face-viewfinder'
                    : 'fingerprint'
                }
              />
            }
          >
            {t(`authentication.biometrics.activate.${biometricsType}`)}
          </Button>
          <Button type="button" variant="tertiary" onPress={onPressDismiss}>
            {t('authentication.biometrics.dismiss')}
          </Button>
        </Stack>
      }
    >
      <Stack gap="xl">
        {biometricsAvailable && (
          <Icon
            icon={
              biometricsType === BiometricsType.FACE_ID
                ? 'face-viewfinder'
                : 'fingerprint'
            }
            size={64}
            color="neutral20"
          />
        )}
        <PageHeader
          title={t(`authentication.biometrics.${biometricsType}.title`)}
          description={t(
            `authentication.biometrics.${biometricsType}.description`
          )}
        />
      </Stack>
    </AuthFormLayout>
  );
}
